import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { FormControl, FormLabel, Radio, RadioGroup } from '@mui/material';
import DialogModal from '../../components/DialogModal';
import { Gender, SnackbarSeverityType } from '../../enums';
import schema from './utils/useValidation';
import { AddUserProps } from './types';
import { DialogContent, AdminAccessSection, StyledTextField } from './styled';
import { initialState } from './utils/utils';
import AdminAccessFields from './components/AdminAccessFields';
import { CreateSchoolOfficeUserRequest } from '../../types/schoolCollection';
import { validateForm } from './utils/validationHelpers';
import userApi from '../../apis/users';
import { StyledRadioButton } from '../common/styled';

const AddSchoolOfficeUserDialog: React.FC<AddUserProps> = ({
  open,
  onClose,
  school,
  onAdd,
  showToast
}) => {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
  }>({});

  const resetForm = () => {
    setForm(initialState);
    setErrors({});
  };

  const validate = async () => {
    const { valid, errors: fieldErrors } = await validateForm(form, schema);
    setErrors(fieldErrors as any);
    return valid;
  };

  const handleChange =
    (field: keyof typeof form) =>
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setForm({ ...form, [field]: evt.target.value });
      if (errors[field as keyof typeof errors])
        setErrors({ ...errors, [field]: undefined });
    };

  const handleAdminAccessChange = (
    key: 'easypay' | 'busTracking' | 'ether',
    value: boolean
  ) => {
    setForm((prev) => ({
      ...prev,
      adminAccess: {
        ...prev.adminAccess,
        [key]: key === 'ether' ? true : value
      }
    }));
  };

  const handleSave = async () => {
    const isValid = await validate();
    if (!isValid || !school) return;

    try {
      setSaving(true);
      const payload: CreateSchoolOfficeUserRequest = {
        name: form.name.trim(),
        email: form.email.trim(),
        gender: form.gender || undefined,
        school
      };
      const response = await userApi.createUser(payload);
      onAdd(response.data);
      showToast('User added successfully', SnackbarSeverityType.success);
      onClose();
      setForm(initialState);
    } catch (error) {
      console.error(error);
      showToast('Failed to add user', SnackbarSeverityType.error);
    } finally {
      setSaving(false);
    }
  };

  const handleDialogClose = () => {
    resetForm();
    onClose();
  };

  const handleGenderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, gender: e.target.value as Gender }));
  };

  const disabled =
    saving ||
    !school ||
    !form.name.trim() ||
    !form.email.trim() ||
    !form.gender;

  return (
    <DialogModal
      open={open}
      onClose={handleDialogClose}
      title='Add User'
      actions={
        <>
          <Button
            variant='contained'
            onClick={handleDialogClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant='contained' onClick={handleSave} disabled={disabled}>
            {saving ? <CircularProgress size={20} color='inherit' /> : 'Save'}
          </Button>
        </>
      }
    >
      <DialogContent>
        <StyledTextField
          label='Name'
          size='small'
          value={form.name}
          onChange={handleChange('name')}
          error={Boolean(errors.name)}
          helperText={errors.name}
        />
        <StyledTextField
          label='Email'
          size='small'
          value={form.email}
          onChange={handleChange('email')}
          error={Boolean(errors.email)}
          helperText={errors.email}
        />
        <FormControl>
          <FormLabel id='gender-radio-group-label'>Gender</FormLabel>
          <RadioGroup
            row
            aria-labelledby='gender-radio-group-label'
            name='gender'
            value={form.gender}
            onChange={handleGenderChange}
            sx={{ gap: 2 }}
          >
            <StyledRadioButton
              value={Gender.MALE}
              control={<Radio size='small' />}
              label='Male'
            />
            <StyledRadioButton
              value={Gender.FEMALE}
              control={<Radio size='small' />}
              label='Female'
            />
          </RadioGroup>
        </FormControl>
        <AdminAccessSection>
          <AdminAccessFields
            adminAccess={form.adminAccess}
            onChange={handleAdminAccessChange}
          />
        </AdminAccessSection>
      </DialogContent>
    </DialogModal>
  );
};

export default AddSchoolOfficeUserDialog;
